class ProductFeature extends HTMLElement {
  connectedCallback() {
    this._moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this._singleQty = 1;
    this._singleUnitPriceCents = parseInt(this.dataset.unitPriceCents || '0', 10) || this._readMoneyFromElement('[data-pf-price]');
    this._singleComparePriceCents = parseInt(this.dataset.comparePriceCents || '0', 10) || 0;
    this._singleSaveCents = parseInt(this.dataset.saveCents || '0', 10) || 0;
    this._isVarietyMode = false;
    this._bundleTotalCents = 0;

    this._initSwiper();
    this._initPackSelector();
    this._initQuantity();
    this._initBundlePricingBridge();

    this._renderPriceRow();
  }

  disconnectedCallback() {
    if (this._swiper) this._swiper.destroy(true, true);
    if (this._onBundleStateChange) {
      document.removeEventListener('pdp:bundle-state-change', this._onBundleStateChange);
    }
  }

  /* ── Swiper ───────────────────────────────────────────────── */
  _initSwiper() {
    const swiperEl = /** @type {HTMLElement | null} */ (this.querySelector('.product-feature__swiper'));
    if (!swiperEl || typeof Swiper === 'undefined') return;

    this._swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      loop: false,
      pagination: {
        el: this.querySelector('.product-feature__swiper-pagination'),
        clickable: true,
      },
    });
  }

  /* ── Pack selector (single / variety) ────────────────────── */
  _initPackSelector() {
    const packItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.product-feature__pack-option'));
    if (!packItems.length) return;

    packItems.forEach(item => {
      item.addEventListener('click', () => {
        packItems.forEach(i => i.classList.remove('product-feature__pack-option--active'));
        item.classList.add('product-feature__pack-option--active');
        this._switchPanel(item.dataset.pack || '');
      });
    });

    // initialise based on the default active button
    const active = /** @type {HTMLElement | null} */ (this.querySelector('.product-feature__pack-option--active'));
    this._switchPanel(active?.dataset.pack || '');
  }

  /* ── Quantity stepper ────────────────────────────────────── */
  _initQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.product-feature__qty-input'));
    const shopNow = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-pf-shop-now]'));
    if (!input || !shopNow) return;

    const baseHref = shopNow.getAttribute('href') || '#';

    const updateHref = () => {
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      const raw = parseInt(input.value, 10);
      const qty = Number.isNaN(raw) ? min : Math.min(Math.max(raw, min), max);
      input.value = String(qty);
      this._singleQty = qty;

      shopNow.href = qty > 1
        ? baseHref + (baseHref.includes('?') ? '&' : '?') + 'quantity=' + qty
        : baseHref;

      this._renderPriceRow();
    };

    this.querySelector('.product-feature__qty-btn--minus')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      const min = parseInt(input.min || '1', 10);
      if (val > min) { input.value = String(val - 1); updateHref(); }
    });

    this.querySelector('.product-feature__qty-btn--plus')?.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      const max = parseInt(input.max || '99', 10);
      if (val < max) { input.value = String(val + 1); updateHref(); }
    });

    input.addEventListener('input', updateHref);
    input.addEventListener('change', updateHref);
    updateHref();
  }

  _initBundlePricingBridge() {
    this._onBundleStateChange = (event) => {
      if (!this._isVarietyMode) return;

      const detail = event?.detail || {};
      if (!Number.isInteger(detail.totalCents)) return;

      this._bundleTotalCents = detail.totalCents;
      this._renderPriceRow();
    };

    document.addEventListener('pdp:bundle-state-change', this._onBundleStateChange);
  }

  _renderPriceRow() {
    if (this._isVarietyMode) {
      const bundleTotalCents = Math.max(this._bundleTotalCents, 0);
      const bundleText = this._formatMoney(bundleTotalCents);

      this.querySelectorAll('[data-pf-price]').forEach((el) => {
        el.textContent = bundleText;
      });

      this.querySelectorAll('[data-pf-compare-price]').forEach((el) => {
        /** @type {HTMLElement} */ (el).style.display = 'none';
      });

      this.querySelectorAll('[data-pf-save-badge]').forEach((el) => {
        /** @type {HTMLElement} */ (el).style.display = 'none';
      });

      return;
    }

    const qty = this._singleQty > 0 ? this._singleQty : 1;
    const totalPriceCents = this._singleUnitPriceCents * qty;
    const totalCompareCents = this._singleComparePriceCents * qty;
    const totalSaveCents = this._singleSaveCents * qty;

    if (totalPriceCents > 0) {
      const totalPriceText = this._formatMoney(totalPriceCents);
      this.querySelectorAll('[data-pf-price]').forEach((el) => {
        el.textContent = totalPriceText;
      });
    }

    this.querySelectorAll('[data-pf-compare-price]').forEach((el) => {
      const compareEl = /** @type {HTMLElement} */ (el);
      const hasCompare = totalCompareCents > totalPriceCents && totalCompareCents > 0;
      compareEl.style.display = hasCompare ? '' : 'none';
      if (hasCompare) {
        compareEl.textContent = this._formatMoney(totalCompareCents);
      }
    });

    this.querySelectorAll('[data-pf-save-badge]').forEach((el) => {
      const saveEl = /** @type {HTMLElement} */ (el);
      if (totalSaveCents > 0) {
        saveEl.textContent = `Save ${this._formatMoney(totalSaveCents)}`;
        saveEl.style.display = '';
      } else {
        saveEl.style.display = 'none';
      }
    });
  }

  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this._moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
  }

  _readMoneyFromElement(selector) {
    const el = this.querySelector(selector);
    if (!(el instanceof HTMLElement)) return 0;

    const text = String(el.textContent || '').replace(/[^\d.,-]/g, '');
    if (!text) return 0;

    const lastComma = text.lastIndexOf(',');
    const lastDot = text.lastIndexOf('.');
    const sep = Math.max(lastComma, lastDot);

    if (sep === -1) {
      const intOnly = parseInt(text.replace(/[^\d-]/g, ''), 10);
      return Number.isNaN(intOnly) ? 0 : intOnly * 100;
    }

    const intPart = text.slice(0, sep).replace(/[^\d-]/g, '');
    const decPart = text.slice(sep + 1).replace(/[^\d]/g, '').slice(0, 2).padEnd(2, '0');
    const cents = parseInt(`${intPart}${decPart}`, 10);
    return Number.isNaN(cents) ? 0 : cents;
  }

  /**
   * Show/hide single vs variety panels.
   * @param {string} pack
   */
  _switchPanel(pack) {
    const isVariety = pack === 'variety';
    this._isVarietyMode = isVariety;

    if (isVariety) {
      this._bundleTotalCents = 0;
    }

    this.querySelectorAll('[data-pf-single-only]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = isVariety ? 'none' : '';
    });
    this.querySelectorAll('[data-pf-variety-only]').forEach(el => {
      /** @type {HTMLElement} */ (el).style.display = isVariety ? '' : 'none';
    });

    this._renderPriceRow();
  }
}

if (!customElements.get('product-feature')) {
  customElements.define('product-feature', ProductFeature);
}
